"use client";

import React, { Fragment } from 'react';
import { X, Plus, Settings, Trash2 } from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';
import { Project, ProjectFormData } from '@/types/project.interface';
import { ProjectPanel } from '@/components/studio/ProjectPanel';
import Api from '@/lib/api';
import { useModal } from '@/redux/useModal';

interface ProjectManagerModalProps {
    isOpen: boolean;
    onClose: () => void;
    profiles: Project[];
    setProfiles: React.Dispatch<React.SetStateAction<Project[]>>;
    editingProject: Project | 'new' | null;
    setEditingProject: (project: Project | 'new' | null) => void;
}

export const ProjectManagerModal: React.FC<ProjectManagerModalProps> = ({
    isOpen,
    onClose,
    profiles,
    setProfiles,
    editingProject,
    setEditingProject,
}) => {
    const modaler = useModal();

    const fetchProjects = async () => {
        try {
            const data = await Api.projects.list();
            setProfiles(Array.isArray(data) ? data : []);
        } catch (error) { console.error("Fetch profiles error:", error); }
    };

    const handleSaveProject = async (data: Partial<Project>) => {
        if (!editingProject || editingProject === 'new') return;
        try {
            const res = await Api.projects.update(editingProject.id, data);
            if ('error' in res) { alert(res.error); return; }
            await fetchProjects();
            setEditingProject(null);
        } catch (error) { console.error("Update project error:", error); }
    };

    const handleCreateProject = async (data: ProjectFormData) => {
        try {
            const res = await Api.projects.create(data);
            if ('error' in res) { alert(res.error); return; }
            await fetchProjects();
            setEditingProject(null);
        } catch (error) { console.error("Create project error:", error); }
    };

    const handleDeleteProject = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await modaler.confirm("确定删除此配置吗？")) return;
        try {
            const res = await Api.projects.delete(id);
            if (res && 'error' in res) { alert(res.error); return; }
            await fetchProjects();
        } catch (error) { console.error("Delete project error:", error); }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-left transition-all w-full max-w-lg flex flex-col max-h-[80vh]">
                                <div className="p-6 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-zinc-50 dark:bg-zinc-900/50 shrink-0">
                                    <Dialog.Title as="h3" className="text-lg font-semibold text-zinc-900 dark:text-white">配置管理</Dialog.Title>
                                    <button onClick={onClose} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full text-zinc-400 dark:text-zinc-500">
                                        <X size={20} />
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                                    {editingProject ? (
                                        <div className="space-y-4">
                                            <button
                                                onClick={() => setEditingProject(null)}
                                                className="text-xs text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 flex items-center gap-1 mb-2 transition-colors"
                                            >
                                                <Plus size={12} className="rotate-45" /> 返回列表
                                            </button>
                                            <ProjectPanel
                                                project={editingProject === 'new' ? null : editingProject}
                                                onSave={handleSaveProject}
                                                onCreateProject={handleCreateProject}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex justify-between items-center">
                                                <p className="text-xs text-zinc-400 dark:text-zinc-500 uppercase font-black tracking-widest">可用配置 ({profiles.length})</p>
                                                <button
                                                    onClick={() => setEditingProject('new')}
                                                    className="px-3 py-1.5 bg-blue-600/10 text-blue-500 dark:text-blue-400 hover:bg-blue-600/20 rounded-lg border border-blue-500/20 text-[10px] font-black transition-all"
                                                >
                                                    + 新增配置
                                                </button>
                                            </div>
                                            <div className="space-y-2">
                                                {profiles.map(p => (
                                                    <div
                                                        key={p.id}
                                                        className="p-4 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/50 rounded-2xl flex justify-between items-center hover:border-zinc-300 dark:hover:border-zinc-500 transition-all group"
                                                    >
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{p.name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => setEditingProject(p)}
                                                                className="p-2 bg-zinc-100 dark:bg-zinc-700/50 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-xl text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-all"
                                                                title="编辑配置"
                                                            >
                                                                <Settings size={14} />
                                                            </button>
                                                            <button
                                                                onClick={(e) => handleDeleteProject(p.id, e)}
                                                                className="p-2 bg-zinc-100 dark:bg-zinc-700/50 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl text-zinc-500 dark:text-zinc-400 hover:text-red-500 dark:hover:text-red-400 transition-all"
                                                                title="删除配置"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                                {profiles.length === 0 && (
                                                    <div className="py-12 text-center border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl">
                                                        <p className="text-xs text-zinc-400 dark:text-zinc-600 italic">暂无已创建的配置。</p>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
